/**
 * Vorlage „Herdstufe einstellen“.
 *
 * Nahaufnahme des Drehknopfs mit der Skala 0 bis 9. Der Knopf dreht auf die
 * gewünschte Stufe, daneben reagiert die Kochstelle: Beim Glaskeramikfeld
 * glüht der Heizring auf, beim Gasherd wachsen die Flammen.
 */
import { ease, lerp, polar, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { ellipsePath, roundedRectPath } from '../paths.ts';
import { circle, compact, ellipse, faded, group, label, line, rect, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { burnerFlames, heatWaves } from '../parts/effects.ts';
import { arcArrow, badge, BADGE_RIGHT, counter, textWidth } from '../parts/stage.ts';
import { heatFromText } from '../parts/stove.ts';
import { choice, defineTemplate, numberValue, textValue } from '../template.ts';

/** Drehknopf und Skala. */
const KNOB = { cx: 62, cy: 82, r: 24 } as const;
const SCALE_R = 40;
/** Kochstelle rechts. */
const HOB = { cx: 180, y: 118, rx: 44, ry: 12 } as const;

/** Zeitplan. */
const TIME = { turn: [0.06, 0.26], heat: [0.2, 0.6], reset: [0.88, 1] } as const;

/** Herdarten. */
const TYPES = { ceramic: 'Glaskeramik / Elektro', gas: 'Gas' } as const;
type HobType = keyof typeof TYPES;

/** Winkel einer Stufe: 0 oben, im Uhrzeigersinn je Stufe 30 Grad. */
function angleOf(level: number): number {
  return -90 + level * 30;
}

/** Skala mit Ziffern; die Zielstufe ist hervorgehoben. */
function dial(target: number): SceneNode[] {
  const nodes: SceneNode[] = [];
  for (let level = 0; level <= 9; level += 1) {
    const at = polar(KNOB.cx, KNOB.cy, SCALE_R, angleOf(level));
    const tick = polar(KNOB.cx, KNOB.cy, KNOB.r + 4, angleOf(level));
    const tickEnd = polar(KNOB.cx, KNOB.cy, KNOB.r + 8, angleOf(level));
    nodes.push(line(tick.x, tick.y, tickEnd.x, tickEnd.y, styles.fine('deep')));
    if (level === target) {
      nodes.push(circle(at.x, at.y, 9, { fill: 'ink', stroke: 'none' }));
    }
    nodes.push(label(at.x, at.y + 4.7, String(level), 13, { fill: level === target ? 'paper' : 'ink' }));
  }
  return nodes;
}

/** Drehknopf mit Griffleiste, gedreht um angle Grad (0 = Markierung oben). */
function knob(angle: number): SceneNode {
  return group([
    circle(KNOB.cx, KNOB.cy, KNOB.r, styles.solid('paper')),
    group(
      [
        shape(
          roundedRectPath(KNOB.cx - 6, KNOB.cy - KNOB.r + 3, 12, KNOB.r * 2 - 6, 5),
          styles.solid('tint'),
        ),
        // Markierung: kräftiger Punkt am oberen Ende der Griffleiste
        circle(KNOB.cx, KNOB.cy - KNOB.r + 9, 3.5, { fill: 'ink', stroke: 'none' }),
      ],
      { transform: { rotate: angle + 90, ox: KNOB.cx, oy: KNOB.cy } },
    ),
  ]);
}

/** Glaskeramikfeld mit Heizring; glow 0 bis 1. */
function ceramic(glow: number, t: number): SceneNode[] {
  const rings = [1, 0.66, 0.34].map((f) => ellipsePath(HOB.cx, HOB.y, HOB.rx * 0.8 * f, HOB.ry * 0.8 * f));
  return [
    shape(roundedRectPath(HOB.cx - HOB.rx - 14, HOB.y - HOB.ry - 6, (HOB.rx + 14) * 2, (HOB.ry + 6) * 2, 6), {
      fill: 'cavity',
      stroke: 'ink',
      strokeWidth: STROKE.outline,
    }),
    ...rings.map((d) => shape(d, { stroke: 'deep', strokeWidth: STROKE.outline })),
    ...rings.map((d) => shape(d, { stroke: 'accent', strokeWidth: STROKE.outline, opacity: glow })),
    ...heatWaves(HOB.cx, HOB.y - 24, t, glow * 0.9, 3, 26),
  ];
}

/** Gasbrenner mit Flammen; power 0 bis 1. */
function gas(power: number, t: number): SceneNode[] {
  return [
    rect(HOB.cx - HOB.rx - 10, HOB.y + 4, (HOB.rx + 10) * 2, 6, 2, styles.solid('shade')),
    rect(HOB.cx - 30, HOB.y - 4, 60, 8, 2, styles.solid('deep')),
    ellipse(HOB.cx, HOB.y - 4, 30, 4, styles.solid('ink')),
    ...burnerFlames(HOB.cx, HOB.y - 6, 52, power, t),
  ];
}

/** Liest die Stufe aus dem Text: „Stufe 6“, „mittlere Hitze“, „Hitze reduzieren“. */
function levelFromText(text: string): number | undefined {
  const lower = text.toLowerCase();
  const explicit = /stufe\s*(\d)/.exec(lower);
  if (explicit?.[1] !== undefined) {
    return Number(explicit[1]);
  }
  if (/höchste|volle|maximal/.test(lower)) return 9;
  if (/reduzier|herunter|zurück|kleiner/.test(lower)) return 3;
  const heat = heatFromText(text).heat;
  if (heat === 'low') return 2;
  if (heat === 'medium') return 5;
  if (heat === 'high') return 8;
  return undefined;
}

/** Vorlage „Herdstufe einstellen“. */
export const stoveSetHeatTemplate = defineTemplate({
  id: 'stove.set-heat',
  group: 'stove',
  title: 'Herdstufe einstellen',
  description:
    'Der Drehknopf wird auf die angegebene Stufe gestellt. Die Kochstelle braucht einen Moment, bis sie die Hitze erreicht.',
  durationSeconds: 5,
  keywords: [
    'stufe',
    'herdstufe',
    'kochstufe',
    'auf stufe',
    'herd einschalten',
    'herd anschalten',
    'herd ausschalten',
    'hitze reduzieren',
    'hitze erhöhen',
    'hitze zurückschalten',
    'herunterschalten',
    'zurückschalten',
    'temperatur reduzieren',
    'auf mittlere hitze',
    'auf hohe hitze',
    'auf kleine flamme',
  ],
  stillTime: 0.7,
  parameters: [
    { kind: 'number', name: 'level', label: 'Stufe', min: 1, max: 9, step: 1, defaultValue: 6 },
    choice('type', 'Herd', TYPES, 'ceramic'),
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    return {
      level: levelFromText(text),
      type: /gas|flamme/.test(lower)
        ? 'gas'
        : /ceran|glaskeramik|elektro|induktion/.test(lower)
          ? 'ceramic'
          : undefined,
    };
  },
  scene(values, t) {
    const target = Math.round(numberValue(values, 'level', 6));
    const type = textValue(values, 'type', 'ceramic') as HobType;
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const turn = ease.inOutCubic(segment(t, ...TIME.turn)) * (1 - reset);
    const current = lerp(0, target, turn);
    const heat = ease.inOutSine(segment(t, ...TIME.heat)) * (1 - reset) * (target / 9);
    const text = `Stufe ${target}`;
    const badgeX = BADGE_RIGHT - (textWidth(text, 15) + 18) / 2;

    return compact([
      ...counter(),
      // Bedienblende
      shape(roundedRectPath(10, KNOB.cy - 54, 104, 108, 10), styles.solid('tint')),
      ...dial(target),
      knob(angleOf(current)),
      // Kontrolllampe
      circle(KNOB.cx + 40, KNOB.cy + 44, 3, { fill: turn > 0.05 ? 'accent' : 'shade', stroke: 'none' }),
      ...(type === 'gas' ? gas(heat, t) : ceramic(heat, t)),
      faded(Math.min(1, turn * 3), [badge(badgeX, 22, text)]),
    ]);
  },
  hints(values) {
    const target = Math.round(numberValue(values, 'level', 6));
    return [arcArrow(KNOB.cx, KNOB.cy, KNOB.r + 14, KNOB.r + 14, angleOf(0) + 8, angleOf(target) - 6)];
  },
});
